/**
 * useServerPing — Pings the server every 5s to measure latency
 *
 * Derives the ping URL from VITE_WS_URL:
 *   wss://uat-ws.summafx.com → https://uat-ws.summafx.com/focus/ping
 *
 * When a user is signed in, the ping carries the Firebase token and the
 * per-tab session id so the backend can register live presence. A 403
 * {action:"logout"} means this session was disconnected by an admin: we
 * sign out silently (the route guard returns the user to the login screen).
 *
 * States:
 *   connected    — ping OK, latency measured
 *   degraded     — 2 consecutive failures (grey icon)
 *   disconnected — 3+ failures or navigator.onLine false (red icon)
 */

import { useEffect, useRef, useCallback } from 'react'
import { useShellStore } from '@/store/shell-store'
import { useAuth } from '@/contexts/AuthContext'
import { getFocusSessionId, clearFocusSessionId } from '@/lib/focusSession'

const PING_INTERVAL = 5_000
const DEGRADED_THRESHOLD = 2
const DISCONNECTED_THRESHOLD = 3

function getPingUrl(): string | null {
  const wsUrl = import.meta.env.VITE_WS_URL
  if (!wsUrl) return null
  // wss://host → https://host/focus/ping, ws://host → http://host/focus/ping
  return wsUrl
    .replace(/^wss:/, 'https:')
    .replace(/^ws:/, 'http:')
    .replace(/\/+$/, '') + '/focus/ping'
}

export function useServerPing() {
  const failCount = useRef(0)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const setConnectionStatus = useShellStore(s => s.setConnectionStatus)
  const setConnectionLatency = useShellStore(s => s.setConnectionLatency)
  const { user, signOut } = useAuth()

  const doPing = useCallback(async () => {
    const pingUrl = getPingUrl()
    if (!pingUrl) return

    // Instant offline detection
    if (!navigator.onLine) {
      failCount.current = DISCONNECTED_THRESHOLD
      setConnectionStatus('disconnected')
      setConnectionLatency(null)
      return
    }

    const headers: Record<string, string> = {}
    if (user) {
      try {
        const token = await user.getIdToken()
        headers['Authorization'] = `Bearer ${token}`
        headers['X-Focus-Session'] = getFocusSessionId()
      } catch {
        // sin token: el ping sigue siendo anónimo, no rompe la latencia
      }
    }

    const start = performance.now()
    try {
      const res = await fetch(pingUrl, {
        method: 'GET',
        cache: 'no-store',
        headers,
        signal: AbortSignal.timeout(4_000),
      })

      // Expulsado por un administrador: cerrar sesión sin mostrar mensaje.
      if (res.status === 403) {
        try {
          const body = await res.json()
          if (body?.action === 'logout') {
            clearFocusSessionId() // nueva identidad de pestaña: el re-login cuenta como sesión nueva
            await signOut()
            return
          }
        } catch {
          // cuerpo no-JSON: tratar como fallo normal más abajo
        }
      }

      if (res.ok) {
        const latency = Math.round(performance.now() - start)
        failCount.current = 0
        setConnectionStatus('connected')
        setConnectionLatency(latency)
      } else {
        throw new Error(`HTTP ${res.status}`)
      }
    } catch {
      failCount.current++
      setConnectionLatency(null)
      if (failCount.current >= DISCONNECTED_THRESHOLD) {
        setConnectionStatus('disconnected')
      } else if (failCount.current >= DEGRADED_THRESHOLD) {
        setConnectionStatus('degraded')
      }
      // 1 failure: keep current status (transient)
    }
  }, [setConnectionStatus, setConnectionLatency, user, signOut])

  useEffect(() => {
    // Initial ping
    doPing()

    // Interval
    timerRef.current = setInterval(doPing, PING_INTERVAL)

    // Online/offline listeners for instant detection
    const goOnline = () => doPing()
    const goOffline = () => {
      failCount.current = DISCONNECTED_THRESHOLD
      setConnectionStatus('disconnected')
      setConnectionLatency(null)
    }

    window.addEventListener('online', goOnline)
    window.addEventListener('offline', goOffline)

    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
      window.removeEventListener('online', goOnline)
      window.removeEventListener('offline', goOffline)
    }
  }, [doPing, setConnectionStatus, setConnectionLatency])
}
